import { createSelector } from '@reduxjs/toolkit';
import { orderBy } from 'lodash';
import {
  selectSelectedProject,
  selectSelectedProjectId,
} from 'store/projects/selectors/projectsSelectors';
import { selectUserActiveEmployerBusinessId } from 'store/user/selectors.js/user.selectors';

const selectOrganizationChartsState = (state) => state.organizationCharts;

export const selectIsOrganizationChartsLoading = createSelector(
  [selectOrganizationChartsState],
  (state) => state.loading
);

export const selectIsModifyOrganizationDialogOpen = createSelector(
  [selectOrganizationChartsState],
  (state) => state.isModifyOrganizationDialogOpen
);

export const selectModifyOrganizationDialogMode = createSelector(
  [selectOrganizationChartsState],
  (state) => state.modifyOrganizationDialogMode
);

export const selectModifyOrganizationTargetId = createSelector(
  [selectOrganizationChartsState],
  (state) => state.modificationTargetId
);

export const selectIsUpdatingOrganizationChart = createSelector(
  [selectOrganizationChartsState],
  (state) => state.isUpdatingOrganizationChart
);

export const selectIsConfirmDeleteOrganizationDialogOpen = createSelector(
  [selectOrganizationChartsState],
  (state) => state.isConfirmDeleteOrganizationDialogOpen
);

export const selectIsConfirmRemoveEmployeeFromProjectDialogOpen =
  createSelector(
    [selectOrganizationChartsState],
    (state) => state.isConfirmRemoveEmployeeFromProjectDialogOpen
  );

export const selectSelectedResourceToRemove = createSelector(
  [selectOrganizationChartsState],
  (state) => state.selectedResourceToRemove
);

export const selectShouldResetOrganizationChart = createSelector(
  [selectOrganizationChartsState],
  (state) => state.shouldResetChart
);

export const selectShouldCenterOnActiveEmployer = createSelector(
  [selectOrganizationChartsState],
  (state) => state.shouldCenterOnActiveEmployer
);

export const selectSelectedChartLayoutDirection = createSelector(
  [selectOrganizationChartsState],
  (state) => state.selectedChartLayoutDirection
);

export const selectOrganizationBusinessIdToAdd = createSelector(
  [selectOrganizationChartsState],
  (state) => state.organizationBusinessIdToAdd
);

export const selectOrganizationBusinessNameToAdd = createSelector(
  [selectOrganizationChartsState],
  (state) => state.organizationBusinessNameToAdd
);

export const selectSubcontractorBusinessAddType = createSelector(
  [selectOrganizationChartsState],
  (state) => state.subcontractorBusinessAddType
);

export const selectIsLoadingBusinessInformation = createSelector(
  [selectOrganizationChartsState],
  (state) => state.isLoadingBusinessInformation
);

export const selectOrganizationChartItemEntities = createSelector(
  [selectOrganizationChartsState],
  (state) => state.organizationChartItems.entities
);

export const selectSearchInputValue = createSelector(
  [selectOrganizationChartsState],
  (state) => state.searchInputValue
);

export const selectSelectedOrganizationChartMode = createSelector(
  [selectOrganizationChartsState],
  (state) => state.selectedOrganizationChartMode
);

export const selectShowEmployeeOptions = createSelector(
  [selectOrganizationChartsState],
  (state) => state.showEmployeeOptions
);

export const selectOrganizationChartItemsArray = createSelector(
  [selectOrganizationChartItemEntities],
  (entities) =>
    Object.values(entities).filter((orgChartItem) => !orgChartItem.deletedAt)
);

export const selectSelectedProjectOrganizationChartItems = createSelector(
  [selectOrganizationChartItemsArray, selectSelectedProjectId],
  (organizationChartItems, selectedProjectId) =>
    organizationChartItems.filter(
      (organizationItem) => organizationItem.projectId === selectedProjectId
    )
);

export const selectModifyOrganizationTargetItem = createSelector(
  [selectOrganizationChartItemEntities, selectModifyOrganizationTargetId],
  (organizationChartEntities, modificationTargetId) =>
    organizationChartEntities[modificationTargetId]
);

export const selectSortedSelectedProjectOrganizationChartItems = createSelector(
  [selectSelectedProjectOrganizationChartItems],
  (organizationChartItems) => {
    return orderBy(
      organizationChartItems,
      [
        (item) =>
          !organizationChartItems.some((child) => child.parentId === item.id),
        (item) => item.businessName.toLowerCase(),
      ],
      ['asc', 'asc']
    );
  }
);

export const selectOrganizationChartSearchMatchOrganizationItemIds =
  createSelector(
    [selectSelectedProjectOrganizationChartItems, selectSearchInputValue],
    (orgItems, searchInput) => {
      if (searchInput) {
        return orgItems
          .filter((orgItem) => {
            const searchableString = `${orgItem.businessName.toLowerCase()}${orgItem.businessId.toLowerCase()}`;

            if (searchableString.includes(searchInput)) {
              return true;
            } else {
              return false;
            }
          })
          .map((orgItem) => orgItem.id);
      } else {
        return [];
      }
    }
  );

export const selectIsActiveEmployerOrganizationApprovedInSelectedProject =
  createSelector(
    [
      selectSelectedProjectOrganizationChartItems,
      selectUserActiveEmployerBusinessId,
      selectSelectedProject,
    ],
    (orgChartItems, activeEmployerBusinessId, selectedProject) => {
      if (selectedProject.business_id === activeEmployerBusinessId) {
        return true;
      }

      const foundOrgChartItemForBusiness = orgChartItems.find(
        (orgItem) => orgItem.businessId === activeEmployerBusinessId
      );

      if (selectedProject.isOrganizationApprovalRequiredForClockIn) {
        if (
          foundOrgChartItemForBusiness &&
          foundOrgChartItemForBusiness.status === 'APPROVED'
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        if (
          foundOrgChartItemForBusiness &&
          foundOrgChartItemForBusiness.status === 'REJECTED'
        ) {
          return false;
        } else {
          return true;
        }
      }
    }
  );

export const selectShowEmployees = createSelector(
  [selectOrganizationChartsState],
  (state) => state.showEmployees
);
